import React from 'react';
import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { NearbyPartner } from '../../../types/additionalServiceTypes';
import PartnerCard from './PartnerCard';

interface Props {
  partners: NearbyPartner[];
  loading: boolean;
  error: string | null;
  serviceName: string;
  serviceId: number | null;
}

function PartnerCardSkeleton() {
  return (
    <Box sx={{ borderRadius: 3, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', p: 2, bgcolor: '#fff' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.25 }}>
        <Skeleton variant="rounded" width={56} height={56} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Skeleton width="55%" height={18} />
            <Skeleton width={48} height={20} sx={{ borderRadius: 4 }} />
          </Stack>
          <Skeleton width="70%" height={13} sx={{ mt: 0.5 }} />
          <Skeleton width={52} height={13} sx={{ mt: 0.5 }} />
        </Box>
      </Stack>

      <Box sx={{ pt: 1.25, borderTop: '1px solid rgba(0,0,0,0.07)' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
          <Skeleton width={68} height={22} />
          <Skeleton width={76} height={13} />
        </Stack>
        <Skeleton variant="rounded" height={30} sx={{ borderRadius: 2, mb: 1 }} />
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" height={28} sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant="rounded" width={60} height={28} sx={{ borderRadius: 2 }} />
        </Stack>
      </Box>
    </Box>
  );
}

function PartnerList({ partners, loading, error, serviceName, serviceId }: Props) {
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: '20px', md: '48px' } }}>
        <Box
          sx={{
            maxWidth: '1100px',
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: '18px',
            pb: '40px',
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <PartnerCardSkeleton key={index} />
          ))}
        </Box>
      </Box>
    );
  }

  if (partners.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
          color: 'text.secondary',
        }}
      >
        <SearchOffIcon sx={{ fontSize: 52, mb: 2, opacity: 0.35 }} />
        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
          Nenhum parceiro encontrado
        </Typography>
        <Typography variant="body2" textAlign="center" color="text.secondary">
          Não há parceiros oferecendo este serviço na sua região.
          <br />
          Tente ampliar o raio de busca.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: '20px', md: '48px' } }}>
      <Box
        sx={{
          maxWidth: '1100px',
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: '18px',
          pb: '40px',
        }}
      >
        {partners.map((partner) => (
          <PartnerCard key={partner.carwash_id} partner={partner} serviceName={serviceName} serviceId={serviceId} />
        ))}
      </Box>
    </Box>
  );
}

export default PartnerList;
